
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBrandPaletteStore, BRAND_PALETTES, DEFAULT_BRAND_PALETTE } from '../stores/brandPaletteStore';
import { useAuth, type Tenant } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useFontSize } from '../context/FontSizeContext';
import { Autocomplete, TextField } from '@mui/material';
import { useToast } from '../hooks/useToast';
import ToastContainer from './feature/ToastContainer';
import { useScreenshotStore } from '../stores/screenshotStore';
import ulbService from '../modules/utility/shared/services/admin/ulbService';
import { queryClient } from '../lib/queryClient';
// Screens that own their UDA scope (Employee Management Setup & Reports, the Asset
// Management dashboard) carry their own in-page UDA dropdown, so this header selector
// must not compete with them.
import { isUdaScopedRoute } from '../modules/business/maud-erp/employee-management/masters/components/udaScope';
// Same arrangement in Asset Management: the dashboard and the registers (work orders,
// disposals, valuations, inspections, encroachments, verify, depreciation, geo-import,
// data quality) each carry their own picker.
import { isAssetUdaScopedRoute } from '../modules/business/maud-erp/asset-management/utils/udaScopedRoute';
// Same arrangement in Leases & Agreements: the dashboard, the transaction screens and
// Reports carry their own UDA dropdown and ignore this one. LAMS Setup is included too,
// for a different reason — it answers to no UDA at all — hence the wider predicate.
import { isLamsHeaderUdaDisabledRoute } from '../modules/business/maud-erp/leases-and-agreements/components/LamsUdaScope';

// A UDA option for the State/Regional user's header selector.
interface UdaOption { uda_id: number; uda_cd?: string; uda_nm: string; tenant_code: string; tenant_id: number; }

// Synthetic "All UDAs" option. Selecting it switches back to the state-level 'default'
// tenant, which returns data across every UDA. tenant_id 0 is a sentinel the backend
// (select-tenant) resolves to the real 'default' tenant; tenant_code 'default' is what
// the backend reports as the user's tenantId while in that context, so this option shows
// as selected whenever the user is in the all-UDAs view.
const ALL_UDAS_OPTION: UdaOption = { uda_id: 0, uda_nm: 'All UDAs', tenant_code: 'default', tenant_id: 0 };

interface HeaderProps {
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  onToggleSidebarCollapse: () => void;
  isSidebarPinned: boolean;
  onToggleSidebarPin: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, isSidebarOpen, isSidebarPinned, onToggleSidebarPin }) => {
  const { user, logout, selectTenant, selectAssignment, roles } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage, isEnglish, isTelugu } = useLanguage();
  const { fontSize, decreaseFont, resetFont, increaseFont } = useFontSize();
  const { takeScreenshots, screenshots } = useScreenshotStore();
  const [capturingShot, setCapturingShot] = useState(false);
  const handleReportIssue = async () => {
    setCapturingShot(true);
    try { await takeScreenshots(); }  // capture only — the floating popup (bottom-right) opens the report form
    finally { setCapturingShot(false); }
  };
  const { toasts, removeToast, error: showErrorToast } = useToast();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<any | null>(null);
  const [isChangingTenant, setIsChangingTenant] = useState(false);
  const [isChangingAssignment, setIsChangingAssignment] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Theme and colours: the shell's brand palette (stores/brandPaletteStore.ts).
  const palette = useBrandPaletteStore((state) => state.palette);
  const setPalette = useBrandPaletteStore((state) => state.setPalette);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const paletteRef = useRef<HTMLDivElement>(null);
  const paletteLabel = BRAND_PALETTES.find((p) => p.key === palette)?.label ?? 'Yellow';

  useEffect(() => {
    if (!isPaletteOpen) return undefined;
    const handleOutside = (event: MouseEvent) => {
      if (paletteRef.current && !paletteRef.current.contains(event.target as Node)) setIsPaletteOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsPaletteOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPaletteOpen]);

  /*
   * The bar wraps onto a second row on narrow screens, so its height is not a
   * constant. It is published as --app-header-h on <html>; the sidebar card and
   * any page that pins something below this bar offset by that variable.
   */
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const publish = () => document.documentElement.style.setProperty('--app-header-h', `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The menu search lives at the top of the sidebar (components/Sidebar.tsx).

  // Get tenants from user
  const tenants = user?.tenants || [];
  const hasMultipleTenants = tenants.length && tenants.length > 1 ? true : false;

  // On Employee Management Setup/Reports, the Asset Management dashboard and registers,
  // and the Leases & Agreements screens, the page has its own UDA dropdown; switching
  // the tenant from here would fight it (and reload the whole session), so the header
  // selector is disabled while any of them is open.
  const location = useLocation();
  const udaSelectorDisabled =
    isUdaScopedRoute(location.pathname) ||
    isAssetUdaScopedRoute(location.pathname) ||
    isLamsHeaderUdaDisabledRoute(location.pathname);

  // State/Regional users (tenantId='default') get a UDA selector sourced from the UDA
  // master (all UDAs). Selecting one switches the working tenant via selectTenant.
  const isStateUser = (user as any)?.isStateRegionalUser || false;
  const [udaList, setUdaList] = useState<UdaOption[]>([]);
  const [selectedUda, setSelectedUda] = useState<UdaOption | null>(null);
  const [loadingUdas, setLoadingUdas] = useState(false);

  useEffect(() => {
    if (!isStateUser) return;
    let cancelled = false;
    setLoadingUdas(true);
    ulbService.getAllUDAs()
      .then((list: any[]) => {
        if (cancelled) return;
        const opts: UdaOption[] = (list || [])
          .filter((u: any) => u?.tenant_id != null && u?.tenant_code != null)
          .map((u: any) => ({
            uda_id: u.uda_id, uda_cd: u.uda_cd, uda_nm: u.uda_nm,
            tenant_code: String(u.tenant_code), tenant_id: Number(u.tenant_id),
          }));
        // "All UDAs" always sits at the top so a state user can switch back to the
        // cross-UDA (default) view even if the per-UDA list is empty.
        setUdaList([ALL_UDAS_OPTION, ...opts]);
      })
      .catch(() => { if (!cancelled) setUdaList([ALL_UDAS_OPTION]); })
      .finally(() => { if (!cancelled) setLoadingUdas(false); });
    return () => { cancelled = true; };
  }, [isStateUser]);

  // Reflect the currently-active UDA (matches the working tenant code).
  useEffect(() => {
    if (udaList.length === 0) { setSelectedUda(null); return; }
    setSelectedUda(udaList.find((u) => u.tenant_code === user?.tenantId) || null);
  }, [udaList, user?.tenantId]);

  /**
   * Switch the working UDA without leaving the page.
   *
   * This used to end in `window.location.reload()`, which threw away the whole SPA —
   * bundle re-download, auth bootstrap, every screen refetched from scratch — for what
   * is really just "same page, other tenant". Instead: the token/user/roles are already
   * swapped by selectTenant, so we drop the cached server state and let the layouts
   * (which key their <Outlet /> on user.tenantId) remount the page content in memory.
   */
  const handleUdaChange = async (uda: UdaOption | null) => {
    if (!uda) return;
    try {
      setIsChangingTenant(true);
      setSelectedUda(uda);
      // Sent as uda_id, not tenant_id: uda_id is what every scoped query in the app
      // uses. The synthetic "All UDAs" row carries uda_id 0, which the server resolves
      // to the state-level 'default' tenant.
      await selectTenant(uda.uda_id, 'uda');
      // Every cached response belongs to the OLD tenant — none of it survives the switch.
      queryClient.clear();
      // For screens that fetch outside TanStack Query and stay mounted across the switch.
      window.dispatchEvent(new CustomEvent('tenant-changed', { detail: { tenantId: uda.tenant_id, tenantCode: uda.tenant_code } }));
    } catch (error: any) {
      console.error('Error selecting UDA:', error);
      showErrorToast(error?.message || 'Failed to switch UDA. Please try again.', 6000);
      setSelectedUda(udaList.find((u) => u.tenant_code === user?.tenantId) || null);
    } finally {
      setIsChangingTenant(false);
    }
  };

  // Show the role switcher only when there is genuinely something to switch between.
  // `roles` spans ALL active assignments (primary or not) — the same list the login
  // picker uses — so a second role reached through a non-primary assignment appears here.
  const hasMultipleRoles = roles && roles.length > 1;

  // Set selected tenant based on tenantId (matching tenant.code)
  useEffect(() => {
    if (user?.tenantId && tenants.length > 0) {
      const currentTenant = tenants.find((t) => t.code === user.tenantId);
      setSelectedTenant(currentTenant || null);
    }
  }, [user?.tenantId, tenants]);

  const handleTenantChange = async (tenant: Tenant | null) => {
    if (!tenant) return;

    try {
      setIsChangingTenant(true);
      setSelectedTenant(tenant);
      await selectTenant(tenant.id);
      // Reload the page to apply new tenant context
      window.location.reload();
    } catch (error: any) {
      console.error('Error selecting tenant:', error);
      // Show error toast to user
      const errorMessage = error?.message || 'Failed to switch tenant. Please try again.';
      showErrorToast(errorMessage, 6000);
      // Revert selection on error
      if (user?.tenantId && tenants.length > 0) {
        const currentTenant = tenants.find((t) => t.code === user.tenantId);
        setSelectedTenant(currentTenant || null);
      }
    } finally {
      setIsChangingTenant(false);
    }
  };

  // Reflect the role the session is currently running as. Matching on the assignment
  // alone is not enough: one position can carry several roles, so the active role id
  // is what distinguishes the entries.
  const findCurrentRole = useCallback(() => {
    if (!roles || roles.length === 0) return null;
    return (
      roles.find(
        (r) =>
          r.assignment_id === user?.primaryAssignment?.assignmentId &&
          Number(r.rle_id) === Number(user?.roleId)
      ) ||
      roles.find((r) => r.assignment_id === user?.primaryAssignment?.assignmentId) ||
      null
    );
  }, [roles, user?.primaryAssignment?.assignmentId, user?.roleId]);

  useEffect(() => {
    setSelectedAssignment(findCurrentRole());
  }, [findCurrentRole]);

  const handleAssignmentChange = async (role: any | null) => {
    if (!role) return;
    // Re-picking the role already in force would cost a full page reload for nothing.
    if (role.assignment_id === selectedAssignment?.assignment_id && role.rle_id === selectedAssignment?.rle_id) {
      setIsDropdownOpen(false);
      return;
    }

    try {
      setIsChangingAssignment(true);
      setSelectedAssignment(role);
      // Pin the exact role, not just the assignment — otherwise a position carrying
      // several roles would hand back whichever menu profile resolved last.
      await selectAssignment(role.assignment_id, role.rle_id);
      // Page will reload automatically in selectAssignment function
    } catch (error: any) {
      console.error('Error selecting role:', error);
      // Show error toast to user
      const errorMessage = error?.message || 'Failed to switch role. Please try again.';
      showErrorToast(errorMessage, 6000);
      // Revert selection on error
      setSelectedAssignment(findCurrentRole());
    } finally {
      setIsChangingAssignment(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
  };

  const handleLanguageSwitch = (targetLanguage: 'en' | 'te') => {
    if (language !== targetLanguage) {
      toggleLanguage();
    }
  };

  const handleProfileClick = () => {
    navigate('/app/profile');
    setIsDropdownOpen(false);
  };

  const formatLastLogin = (dateString?: string) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch {
      return 'Invalid date';
    }
  };

  const getUserInitial = () => {
    const name = (user as any)?.name || (user as any)?.username;
    if (name) return name.charAt(0).toUpperCase();
    return 'U';
  };

  const getUserDisplayName = () => {
    return (user as any)?.name || (user as any)?.username || 'User';
  };

  const getUserEmail = () => {
    return (user as any)?.email || 'No email';
  };

  // Get icon based on hierarchy type
  const getHierarchyIcon = (hierarchyType: 'state' | 'regional' | 'ulb' | 'ward' | 'sachivalayam' | null): string => {
    switch (hierarchyType) {
      case 'state':
        return 'ri-government-line';
      case 'regional':
        return 'ri-map-line';
      case 'ulb':
        return 'ri-building-line';
      case 'ward':
        return 'ri-home-line';
      case 'sachivalayam':
        return 'ri-file-list-line';
      default:
        return 'ri-building-line';
    }
  };

  // Determine hierarchy type based on which boundary is displayed
  const getDisplayBoundaryType = (hierarchy: any): 'state' | 'regional' | 'ulb' | 'ward' | 'sachivalayam' | null => {
    if (!hierarchy) return null;
    // Priority order: most specific first - sachivalayam > ward > ulb > regional > state
    if (hierarchy.sachivalayam) return 'sachivalayam';
    if (hierarchy.ward) return 'ward';
    if (hierarchy.ulb) return 'ulb';
    if (hierarchy.regional) return 'regional';
    if (hierarchy.state) return 'state';
    return null;
  };

  return (
    <header
      ref={headerRef}
      /*
       * PINNED, IN EVERY SHELL, AND FULL WIDTH.
       *
       * The shells render this bar above the sidebar/content row, so it spans
       * the window. It wraps onto a second row on narrow screens; its live
       * height is published as --app-header-h (see the layout effect above).
       *
       * Deliberately NOT tagged data-theme="header": index.css recolours every
       * descendant of that attribute for the old dark bar, which would fight
       * the white search pill and user chip here. The brown is painted with
       * backgroundImage because themeService writes backgroundColor inline on
       * every <header> a tick after render; it never touches backgroundImage.
       */
      className="dtcp-topbar sticky top-0 z-[100] flex flex-wrap items-center gap-x-4 gap-y-2.5 px-3 sm:px-5 py-3 text-white"
      style={{
        backgroundImage: 'linear-gradient(90deg, var(--brand-1) 0%, var(--brand-2) 55%, var(--brand-3) 100%)',
        fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
        boxShadow: '0 2px 12px rgba(60, 35, 5, 0.18)',
      }}
    >
      {/* Left Section */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile Menu Toggle */}
        <button
          onClick={onToggleSidebar}
          className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors lg:hidden hover:bg-white/15"
          aria-label="Open menu"
        >
          <i className="ri-menu-line text-xl text-white"></i>
        </button>

        {/* Department identity: the state seal and the name. */}
        <img
          src="/images/header/ap-emblem-sidebar.png"
          alt="Andhra Pradesh State Emblem"
          /* The seal on its own — the PNG is transparent outside the seal, so no
             disc or ring behind it. Height-led at its natural 142:160 ratio; the
             shadow seats it on the coloured bar. */
          className="h-[54px] w-auto object-contain flex-shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
          draggable={false}
        />
        {/* The heading is a touch larger and heavier than the bar's other text,
            with a soft shadow so it lifts off the gradient; the subtitle steps up
            with it so the pair keeps its proportions. */}
        <div className="flex flex-col min-w-0 leading-tight">
          <span
            className="text-[21px] font-extrabold tracking-[0.01em] whitespace-nowrap text-white"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.35), 0 0 14px rgba(0,0,0,0.18)' }}
          >
            DTCP <span className="text-[color:var(--brand-accent)]">Andhra Pradesh</span>
          </span>
          <span
            className="text-[12px] font-medium whitespace-nowrap text-white/90 mt-0.5"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
          >
            Directorate of Town &amp; Country Planning
          </span>
        </div>

        {/* UDA Selector - shown for State/Regional users (lists all UDAs).
            Hidden per requirement — flip `false &&` back to re-enable the header
            selector. The scope-switching logic (handleUdaChange / selectTenant)
            is left intact; only the picker is removed from the header. */}
        {false && isStateUser && udaList.length > 0 && (
          <div
            className="ml-1 sm:ml-4 flex items-center"
            title={
              // One message for every screen that owns its scope. The asset dashboard
              // used to get a variant pointing at its Filters panel, from when that
              // panel held its only UDA field; it now carries a visible picker on the
              // assets grid toolbar, as the registers do in their page headers, so
              // "on the page" is true everywhere again.
              udaSelectorDisabled
                ? 'This screen has its own UDA selector — use the UDA dropdown on the page.'
                : undefined
            }
          >
            <div className="w-32 sm:w-44">
              <Autocomplete
                options={udaList}
                getOptionLabel={(option) => option.uda_nm || ''}
                isOptionEqualToValue={(option, value) => !!option && !!value && option.tenant_id === value.tenant_id}
                value={selectedUda}
                onChange={(_, newValue) => handleUdaChange(newValue)}
                loading={isChangingTenant || loadingUdas}
                disabled={isChangingTenant || udaSelectorDisabled}
                size="small"
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: isDark ? 'rgb(31 41 55)' : '#ffffff',
                      /*
                       * The list is portalled to <body>, so it is NOT on the bar and
                       * must not borrow the bar's foreground: --theme-header-fg is
                       * light in both themes and would be pale-on-white here. This
                       * one follows the app's surface, unlike the field above it.
                       */
                      color: isDark ? '#f3f4f6' : '#111827',
                      border: isDark ? '1px solid rgb(55 65 81)' : '1px solid #e5e7eb',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                      '& .MuiAutocomplete-listbox': {
                        padding: '6px',
                        '& .MuiAutocomplete-option': {
                          borderRadius: '8px',
                          fontSize: '0.8rem',
                          padding: '8px 10px',
                          minHeight: 'auto',
                          '&:hover': { backgroundColor: isDark ? 'rgba(124,58,237,0.16)' : 'rgba(124,58,237,0.10)' },
                          '&[aria-selected="true"]': {
                            backgroundColor: isDark ? 'rgba(124,58,237,0.28)' : 'rgba(124,58,237,0.16)',
                            color: isDark ? '#fde68a' : '#92400e',
                            fontWeight: 600,
                          },
                          '&[aria-selected="true"]:hover': { backgroundColor: isDark ? 'rgba(124,58,237,0.34)' : 'rgba(124,58,237,0.22)' },
                        },
                      },
                      '& .MuiAutocomplete-noOptions': { fontSize: '0.8rem', color: isDark ? '#9ca3af' : '#6b7280' },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={isChangingTenant ? 'Switching...' : (loadingUdas ? 'Loading UDAs...' : 'All UDAs')}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <i className="ri-community-line text-base ml-2 mr-1" style={{ color: 'var(--theme-header-fg-muted)' }}></i>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 32,
                        borderRadius: '8px',
                        paddingLeft: '4px',
                        transition: 'background-color .15s ease, box-shadow .15s ease',
                        /*
                         * The field sits ON the bar, so it is translucent white over
                         * the bar's own surface in both themes — not a light-mode
                         * grey pill. Its text is --theme-header-fg, which is light in
                         * both themes; against the old '#f9fafb' that was pale-on-pale
                         * and the selection read as an empty box.
                         */
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                        '&:hover fieldset': { borderColor: '#a78bfa' },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255,255,255,0.18)',
                          boxShadow: '0 0 0 3px rgba(167,139,250,0.28)',
                        },
                        '&.Mui-focused fieldset': { borderColor: '#a78bfa', borderWidth: '1.5px' },
                        '& .MuiInputBase-input': {
                          color: 'var(--theme-header-fg)',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          padding: '4px 6px !important',
                        },
                        '& .MuiInputBase-input::placeholder': { color: 'var(--theme-header-fg-muted)', opacity: 1 },
                      },
                      '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: 'var(--theme-header-fg-muted)' },
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}

        {/* Tenant Dropdown - Hidden per requirement */}
        {false && hasMultipleTenants && tenants.length > 0 && (
          <div className="ml-1 sm:ml-4 flex items-center">
            <div className="w-40 sm:w-56 md:w-72">
              <Autocomplete
                options={tenants}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={selectedTenant}
                onChange={(_, newValue) => handleTenantChange(newValue)}
                loading={isChangingTenant}
                disabled={isChangingTenant}
                size="small"
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: isDark ? 'rgb(31 41 55)' : 'white',
                      color: isDark ? 'white' : 'inherit',
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={isChangingTenant ? 'Switching...' : 'Select Tenant'}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <i className="ri-building-line text-gray-400 ml-2 mr-1"></i>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? 'rgb(31 41 55)' : 'white',
                        '& fieldset': {
                          borderColor: isDark ? 'rgb(75 85 99)' : 'rgb(209 213 219)',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? 'rgb(107 114 128)' : 'rgb(156 163 175)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#7c3aed',
                          borderWidth: '2px',
                        },
                        '& .MuiInputBase-input': {
                          color: isDark ? 'white' : 'inherit',
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '6px 14px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: isDark ? 'rgb(156 163 128)' : 'inherit',
                        opacity: 1,
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props;
                  return (
                    <li key={key} {...restProps}>
                      <div className="flex items-center py-1 w-full">
                        <i className="ri-building-line mr-2 text-gray-400"></i>
                        <span>{option.name}</span>
                      </div>
                    </li>
                  );
                }}
              />
            </div>
          </div>
        )}

        {/* Current Tenant/Boundary Name - Hidden per requirement */}
        {false && !hasMultipleTenants && (() => {
          const hierarchy = user?.primaryBoundary;

          // For sachivalayam/ward level users, show their specific boundary name
          if (hierarchy?.sachivalayam || hierarchy?.ward) {
            const displayBoundary = hierarchy.sachivalayam || hierarchy.ward;
            const hierarchyType = getDisplayBoundaryType(hierarchy);
            const iconClass = getHierarchyIcon(hierarchyType);

            return (
              <div className="ml-1 sm:ml-4 flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 max-w-[140px] sm:max-w-none">
                <i className={`${iconClass} text-gray-500 dark:text-gray-400 mr-1 sm:mr-2 flex-shrink-0`}></i>
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {displayBoundary?.name} {displayBoundary?.code ? `(${displayBoundary?.code})` : ''}
                </span>
              </div>
            );
          }

          // Show current tenant name if available (for ULB level users)
          if (selectedTenant) {
            return (
              <div className="ml-1 sm:ml-4 flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 max-w-[140px] sm:max-w-none">
                <i className="ri-building-line text-gray-500 dark:text-gray-400 mr-1 sm:mr-2 flex-shrink-0"></i>
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {selectedTenant?.name}
                </span>
              </div>
            );
          }

          // Fall back to boundary display
          if (!hierarchy) return null;

          // For users without tenant info, show the most specific boundary first
          // Priority: sachivalayam > ward > ulb > regional > state
          const displayBoundary = hierarchy.sachivalayam || hierarchy.ward || hierarchy.ulb || hierarchy.regional || hierarchy.state;
          const hierarchyType = getDisplayBoundaryType(hierarchy);
          const iconClass = getHierarchyIcon(hierarchyType);

          return displayBoundary ? (
            <div className="ml-1 sm:ml-4 flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 max-w-[140px] sm:max-w-none">
              <i className={`${iconClass} text-gray-500 dark:text-gray-400 mr-1 sm:mr-2 flex-shrink-0`}></i>
              <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {displayBoundary.name} {displayBoundary?.code ? `(${displayBoundary?.code})` : ''}
              </span>
              {displayBoundary.type && (
                <span className="hidden sm:inline ml-2 text-xs text-gray-500 dark:text-gray-400">
                  ({displayBoundary.type})
                </span>
              )}
            </div>
          ) : null;
        })()}


      </div>

      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        {/* Report Issue — captures a screenshot of the current page and opens the New Ticket form */}
        <button
          data-html2canvas-ignore="true"
          onClick={handleReportIssue}
          className="relative inline-flex items-center justify-center w-[34px] h-[34px] rounded-full transition-colors bg-white/10 border border-white/25 hover:bg-white/20"
          title="Report an issue (captures a screenshot of this screen)"
        >
          <i className={`${capturingShot ? 'ri-camera-fill animate-pulse' : 'ri-customer-service-2-line'} text-[15px] text-white`}></i>
          {screenshots.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{screenshots.length}</span>
          )}
        </button>

        {/* Text size — 14 / 16 / 18px root font (FontSizeContext) */}
        <div className="inline-flex items-center gap-1" role="group" aria-label="Text size">
          <span className="text-[13px] font-semibold text-white/85 px-1" aria-hidden="true">Aa</span>
          {([
            { key: 'small', label: 'A−', title: 'Decrease text size', onClick: decreaseFont },
            { key: 'medium', label: 'A', title: 'Default text size', onClick: resetFont },
            { key: 'large', label: 'A+', title: 'Increase text size', onClick: increaseFont },
          ] as const).map((opt) => {
            const active = fontSize === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={opt.onClick}
                title={opt.title}
                aria-pressed={active}
                className={`w-[30px] h-[30px] inline-flex items-center justify-center rounded-full text-[12px] font-bold tabular-nums transition-colors ${
                  active ? 'bg-[color:var(--brand-accent)] text-[#2A1D0E]' : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Theme and colours — brand palette picker */}
        <div className="relative" ref={paletteRef}>
          <button
            type="button"
            onClick={() => setIsPaletteOpen((open) => !open)}
            className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-full bg-white transition-transform hover:scale-105"
            title="Theme and colours"
            aria-label="Theme and colours"
            aria-haspopup="dialog"
            aria-expanded={isPaletteOpen}
          >
            <span
              aria-hidden="true"
              className="w-[22px] h-[22px] rounded-full"
              style={{ background: 'conic-gradient(#D7261E, #F2BD4A, #2F8F5B, #137C74, #1F3B73, #7B4FC9, #D7261E)' }}
            />
          </button>

          {isPaletteOpen && (
            <div
              role="dialog"
              aria-label="Theme and colours"
              className="absolute right-0 top-full mt-2 z-[99999] w-[272px] rounded-2xl bg-white text-[#2A1D0E] shadow-[0_12px_32px_rgba(60,35,5,0.22)] border border-[#ECE2D3] dark:bg-[#241A10] dark:text-[#F3EAD9] dark:border-[#3A2C1C]"
            >
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#ECE2D3] dark:border-[#3A2C1C]">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[color:var(--brand-cream)] text-[color:var(--brand-deep)] dark:bg-[#3A2C1C] dark:text-[color:var(--brand-accent)]">
                  <i className="ri-palette-line text-base"></i>
                </span>
                <span className="leading-tight">
                  <span className="block text-[14px] font-semibold">Theme and colours</span>
                  <span className="block text-[12px] text-[#7A6A56] dark:text-[#B3A48F]">{paletteLabel}</span>
                </span>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[11px] font-bold tracking-[0.1em] text-[#7A6A56] dark:text-[#B3A48F]">PALETTES</span>
                  <button
                    type="button"
                    onClick={() => setPalette(DEFAULT_BRAND_PALETTE)}
                    disabled={palette === DEFAULT_BRAND_PALETTE}
                    className="text-[12px] font-medium text-[#2A1D0E] hover:underline disabled:opacity-50 disabled:no-underline dark:text-[#F3EAD9]"
                  >
                    Use default theme
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {BRAND_PALETTES.map((p) => {
                    const selected = p.key === palette;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setPalette(p.key)}
                        title={p.label}
                        aria-label={`${p.label} palette`}
                        aria-pressed={selected}
                        className={`h-[30px] rounded-lg flex items-center justify-center transition-transform hover:scale-105 ${
                          selected ? 'ring-2 ring-offset-2 ring-[#2A1D0E] dark:ring-[#F3EAD9] dark:ring-offset-[#241A10]' : ''
                        }`}
                        style={{ backgroundColor: p.swatch }}
                      >
                        {selected && (
                          <i className={`ri-check-line text-[15px] ${p.key === 'yellow' || p.key === 'saffron' ? 'text-[#2A1D0E]' : 'text-white'}`}></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Theme Toggle — circular button */}
        <button
          onClick={toggleTheme}
          className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-full transition-colors bg-white/15 border border-white/25 hover:bg-white/25"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <i className={`ri-${isDark ? 'sun' : 'moon'}-line text-[15px] text-white`}></i>
        </button>

        {/* User Menu */}
        {/* Opens on click only; a click outside closes it. */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2.5 pl-1.5 pr-2.5 py-1.5 rounded-xl bg-white text-[#2A1D0E] transition-shadow hover:shadow-md dark:bg-[#241A10] dark:text-[#F3EAD9]"
            title="Account menu"
            aria-expanded={isDropdownOpen}
          >
            <span className="w-8 h-8 rounded-full bg-[color:var(--brand-accent)] flex items-center justify-center font-bold text-[14px] text-[#2A1D0E] flex-shrink-0">
              {getUserInitial()}
            </span>
            <span className="hidden sm:flex flex-col items-start leading-tight min-w-0 max-w-[180px]">
              <span className="text-[14px] font-bold truncate max-w-full">{getUserDisplayName()}</span>
              {(user as any)?.roleName && (
                <span className="text-[11px] text-[#7A6A56] dark:text-[#B3A48F] truncate max-w-full">{(user as any).roleName}</span>
              )}
            </span>
            <i className={`ri-arrow-down-s-line text-base text-[#7A6A56] dark:text-[#B3A48F] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isDropdownOpen && (
            // profile popup modal - z-index must be higher than all other page elements
            <div className="absolute right-0 top-full pt-2 z-[99999]">
             <div className="w-72 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="py-1">
                <div className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                  <div className="font-medium text-gray-900 dark:text-white">{getUserDisplayName()}</div>
                  {(user as any)?.primaryAssignment?.designationName && (
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">
                      {(user as any).primaryAssignment.designationName}
                    </div>
                  )}
                  <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                    Last login: {formatLastLogin((user as any)?.lastLoginDate)}
                  </div>
                </div>

                {/* Role switcher — sits directly under the name because the role IS part of
                    who you are signed in as, alongside the designation above it. Only rendered
                    when the user actually holds more than one role; everyone else sees the
                    account menu exactly as before.

                    Deliberately an inline list rather than a nested popover: a dropdown inside
                    a dropdown costs an extra click and traps focus awkwardly, and the real data
                    is two roles per employee. */}
                {hasMultipleRoles && (
                  <div className="border-b border-gray-200 dark:border-gray-700 px-3 pt-2 pb-3">
                    <p className="pb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Switch role
                    </p>
                    {/* Bordered container so the switcher reads as a control in its own
                        right, not as two more menu rows blending into Profile/Logout. */}
                    <ul className="max-h-64 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden">
                      {roles.map((role) => {
                        const isActive =
                          role.assignment_id === selectedAssignment?.assignment_id &&
                          role.rle_id === selectedAssignment?.rle_id;
                        return (
                          <li key={`${role.assignment_id}-${role.rle_id}`}>
                            <button
                              type="button"
                              role="menuitemradio"
                              aria-checked={isActive}
                              disabled={isChangingAssignment}
                              onClick={() => handleAssignmentChange(role)}
                              className={`w-full text-left px-3 py-2 flex items-start gap-2.5 transition-colors disabled:opacity-60 disabled:cursor-wait ${
                                isActive
                                  ? 'bg-violet-50 dark:bg-violet-900/20 border-l-[3px] border-violet-500 pl-[9px]'
                                  : 'hover:bg-gray-100 dark:hover:bg-gray-700/60'
                              }`}
                            >
                              <i
                                className={`mt-0.5 text-base shrink-0 ${
                                  isChangingAssignment && isActive
                                    ? 'ri-loader-4-line animate-spin text-violet-600 dark:text-violet-400'
                                    : isActive
                                      ? 'ri-checkbox-circle-fill text-violet-600 dark:text-violet-400'
                                      : 'ri-circle-line text-gray-300 dark:text-gray-600'
                                }`}
                              ></i>
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1.5">
                                  <span
                                    className={`text-sm font-semibold truncate ${
                                      isActive
                                        ? 'text-violet-700 dark:text-violet-300'
                                        : 'text-gray-900 dark:text-gray-100'
                                    }`}
                                  >
                                    {role.rle_nm}
                                  </span>
                                  {role.is_primary && (
                                    <span className="shrink-0 rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                      Primary
                                    </span>
                                  )}
                                </span>
                                {/* The designation is what the employee recognises day to day;
                                    the role name above is the RBAC label driving the menus. */}
                                <span className="block text-xs text-gray-600 dark:text-gray-400 truncate">
                                  {role.designation_name || role.position_name}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                <button
                  onClick={handleProfileClick}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center"
                >
                  <i className="ri-user-line mr-2"></i>
                  Profile
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 flex items-center"
                >
                  <i className="ri-logout-box-line mr-2"></i>
                  Logout
                </button>
              </div>
             </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Container for Header-specific toasts */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </header>
  );
};

export default Header;
